/**
 * src/hooks/useHorizontalScroll.js — horizontal scrollers (pill bars, tab bars, kanban rows)
 * Attach to any horizontally-scrollable container:
 *   const ref = useHorizontalScroll();  <div ref={ref} style={{ overflowX: 'auto' }}>
 *
 * It does three things:
 *  1. Mouse wheel → horizontal scroll (desktop), bound non-passive so preventDefault works.
 *  2. AFFORDANCE: while the row overflows, a mask-image edge fade on the side(s) that hold
 *     more content. A phone has no scrollbar and no hover arrows, so a 9-tab strip that
 *     showed 3 tabs read as a 3-tab strip — the rest were unreachable in practice (mobile
 *     overflow audit, tests/e2e/mobile-overflow-audit.spec.js). The fade draws nothing
 *     tappable, so it cannot overlap or steal a tap from the controls under it.
 *  3. The selected child (SELECTED: [aria-current="true"] on a tab-like button, or
 *     [aria-selected="true"] in a real tablist) is scrolled fully into view (clear of the
 *     fade) whenever the selection changes, so the active tab is never left off-screen.
 *
 * The ref may attach late (a view that renders a skeleton first): attachment is re-checked
 * after every render instead of once on mount.
 */
import { useRef, useEffect } from 'react';

export const EDGE_FADE_PX = 24;
export const SELECTED = '[aria-current="true"], [aria-selected="true"]';

/** Set / clear the edge-fade mask for the element's current scroll position. */
export function applyEdgeFade(el) {
  if (!el) return;
  const max = el.scrollWidth - el.clientWidth;
  let mask = '';
  let edges = '';
  if (max > 1) {
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft >= max - 1;
    mask = `linear-gradient(to right, ${atStart ? 'black' : 'transparent'} 0, black ${EDGE_FADE_PX}px, `
      + `black calc(100% - ${EDGE_FADE_PX}px), ${atEnd ? 'black' : 'transparent'} 100%)`;
    edges = [!atStart && 'start', !atEnd && 'end'].filter(Boolean).join(' ');
  }
  // data-scroll-fade names the faded edge(s) — the mask is a pure function of it, so it is
  // also the change detector (and readable where CSS masks are not, e.g. jsdom).
  if ((el.getAttribute('data-scroll-fade') || '') === edges) return;
  el.style.maskImage = mask;
  el.style.webkitMaskImage = mask;
  if (edges) el.setAttribute('data-scroll-fade', edges);
  else el.removeAttribute('data-scroll-fade');
}

/** Scroll the selected child horizontally until it sits fully inside the un-faded area. */
export function revealSelected(el) {
  const active = el?.querySelector(SELECTED);
  if (!active) return;
  const box = el.getBoundingClientRect();
  const a = active.getBoundingClientRect();
  const pad = el.scrollWidth - el.clientWidth > 1 ? EDGE_FADE_PX : 0;
  if (a.left < box.left + pad) el.scrollLeft -= box.left + pad - a.left;
  else if (a.right > box.right - pad) el.scrollLeft += a.right - (box.right - pad);
}

/**
 * Wire the affordance (fade + selected-in-view) to an element. Returns a cleanup.
 * Exported for components that own their scroll container (ScrollableRow).
 */
export function attachScrollAffordance(el) {
  if (!el) return () => {};
  let lastSelected = null;
  let lastWidth = -1;
  const update = () => applyEdgeFade(el);
  // Issue #1981: reveal again when the CONTENT width changes, not only the selection. The
  // selected label turns bold after the click, the row grows a few px, and the tab that was
  // just revealed slid back past the edge (Procurement "Supplier Network", Analytics
  // "Voice Pipeline"; measured max scroll 492 -> 496 with scrollLeft stuck at 492).
  const sync = () => {
    const selected = el.querySelector(SELECTED);
    const width = el.scrollWidth;
    if (selected && (selected !== lastSelected || width !== lastWidth)) revealSelected(el);
    lastSelected = selected;
    lastWidth = width;
    update();
  };
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(sync) : null;
  // The row's own box rarely changes when a child widens -- watch the children too.
  const observeChildren = () => { if (ro) for (const c of el.children) ro.observe(c); };
  sync();
  el.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', sync);
  ro?.observe(el);
  observeChildren();
  const mo = typeof MutationObserver === 'function'
    ? new MutationObserver(() => { observeChildren(); sync(); })
    : null;
  mo?.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-current', 'aria-selected'] });
  return () => {
    el.removeEventListener('scroll', update);
    window.removeEventListener('resize', sync);
    ro?.disconnect();
    mo?.disconnect();
  };
}

export function useScrollAffordance(ref) {
  const attached = useRef({ el: null, cleanup: null });
  useEffect(() => {
    const el = ref.current;
    if (el === attached.current.el) return;
    attached.current.cleanup?.();
    attached.current = { el, cleanup: attachScrollAffordance(el) };
  });
  useEffect(() => () => attached.current.cleanup?.(), []);
}

export default function useHorizontalScroll() {
  const ref = useRef(null);
  const wheel = useRef({ el: null, cleanup: null });
  useEffect(() => {
    const el = ref.current;
    if (el === wheel.current.el) return;
    wheel.current.cleanup?.();
    let cleanup = null;
    if (el) {
      const handler = (e) => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }
      };
      el.addEventListener('wheel', handler, { passive: false });
      cleanup = () => el.removeEventListener('wheel', handler);
    }
    wheel.current = { el, cleanup };
  });
  useEffect(() => () => wheel.current.cleanup?.(), []);
  useScrollAffordance(ref);
  return ref;
}
